import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../../Assets/Icon/CustomIcon';

function DropdownMenu({
  anchorRect,
  options,
  selectedLabel,
  onSelect,
  onClose,
}) {
  const [direction, setDirection] = useState('down');

  useEffect(() => {
    const spaceBelow = window.innerHeight - anchorRect.bottom;
    setDirection(spaceBelow < 180 ? 'up' : 'down');
  }, [anchorRect]);

  const style = {
    position: 'fixed',
    left: anchorRect.left + anchorRect.width / 2,
    transform: 'translateX(-50%)',
    zIndex: 9999,
    minWidth: 120,
    maxHeight: 240,
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: '#cbd5e1 transparent',
    ...(direction === 'up'
      ? { bottom: window.innerHeight - anchorRect.top + 8 }
      : { top: anchorRect.bottom + 8 }),
  };

  return createPortal(
    <>
      {/* 스크롤바 스타일 직접 주입 */}
      <style>{`
        .filter-dropdown::-webkit-scrollbar {
          display: block;
          width: 6px;
        }
        .filter-dropdown::-webkit-scrollbar-track {
          background: transparent;
        }
        .filter-dropdown::-webkit-scrollbar-thumb {
          background-color: #cbd5e1;
          border-radius: 999px;
        }
      `}</style>

      <div
        className="fixed inset-0"
        style={{ zIndex: 9998 }}
        onClick={onClose}
      />
      <div
        style={style}
        className="filter-dropdown rounded-[10px] border border-[#e1e8ed] bg-white shadow-lg"
      >
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => {
              onSelect(opt);
              onClose();
            }}
            className="flex w-full items-center justify-between gap-2 border-b border-slate-50 px-4 py-2.5 transition-colors last:border-none hover:bg-gray-50"
          >
            <span
              className={`text-[12px] ${
                selectedLabel === opt.label
                  ? 'font-bold text-[#77b054]'
                  : 'text-slate-600'
              }`}
            >
              {opt.label}
            </span>
            {selectedLabel === opt.label && (
              <Icon.Check className="w-3.5 text-[#77b054]" />
            )}
          </button>
        ))}
      </div>
    </>,
    document.body
  );
}

function Filter({ items = [], optionsByKey = {}, onSelect, className = '' }) {
  const [openKey, setOpenKey] = useState(null);
  const [anchorRect, setAnchorRect] = useState(null);
  const buttonRefs = useRef({});

  const handleButtonClick = (key) => {
    if (openKey === key) {
      setOpenKey(null);
      return;
    }
    const rect = buttonRefs.current[key]?.getBoundingClientRect();
    if (rect) {
      setAnchorRect(rect);
      setOpenKey(key);
    }
  };

  if (items.length === 0) return null;

  const innerPadding = className.includes('p-0') ? '' : 'px-4';

  return (
    <div className={`shrink-0 py-1 ${className}`.trim()}>
      <div
        className={innerPadding}
        style={{
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div
          className="flex flex-nowrap gap-2"
          style={{ width: 'max-content' }}
        >
          {items.map((item) => (
            <div key={item.key} className="relative">
              <button
                ref={(el) => (buttonRefs.current[item.key] = el)}
                type="button"
                onClick={() => handleButtonClick(item.key)}
                className="group flex min-w-21 items-center justify-between rounded-full border border-[#e1e8ed] bg-white py-1.5 pr-1.5 pl-3 shadow-sm transition-all hover:border-[#77b054]/50"
              >
                <span className="text-[12px] font-medium text-slate-700">
                  {item.label}:{' '}
                  <span className="font-bold text-[#77b054]">
                    {item.value ?? '전체'}
                  </span>
                </span>
                <Icon.ArrowBottom
                  className={`text-[#7f8c8d] transition-transform group-hover:text-[#77b054] ${
                    openKey === item.key ? 'rotate-180' : ''
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>

      {openKey && anchorRect && optionsByKey[openKey] && (
        <DropdownMenu
          anchorRect={anchorRect}
          options={optionsByKey[openKey]}
          selectedLabel={items.find((i) => i.key === openKey)?.value}
          onSelect={(opt) => onSelect(openKey, opt)}
          onClose={() => setOpenKey(null)}
        />
      )}
    </div>
  );
}

export default Filter;
